/**
 * [MARIO] v1.0 | 2026-10-16 | OpenAI (ChatGPT) Ads — storefront funnel events
 * Client: SSUNSU (ssunsu.in) | File: assets/oaiq-events.js
 *
 * Fires: items_added (any AJAX /cart/add — fetch or XHR, incl. cart drawer),
 *        checkout_started (cart/drawer "Check out" click, before Fastrr opens),
 *        items_added + checkout_started (Fastrr "BUY NOW" click).
 * Also copies the Pixel's __oppref / __obref cookies into hidden cart attributes
 * (_oai_oppref / _oai_obref) so the server-side order_created event can be
 * attributed. Whether Fastrr carries cart attributes onto the Shopify order is
 * NOT confirmed yet — verify on the test order.
 *
 * Depends on: snippets/oaiq-pixel.liquid (window.oaiq + window.SSUNSU_OAIQ).
 */
(() => {
  'use strict';

  const CFG = window.SSUNSU_OAIQ || {};
  if (typeof window.oaiq !== 'function' || window.__ssunsuOaiqBound) return;
  window.__ssunsuOaiqBound = true;

  const CURRENCY = CFG.currency || 'INR';
  const ROOT = CFG.root || '/';
  const CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href$="/checkout"]',
    'a[href*="/checkout?"]',
    ...(CFG.checkoutSelectors || []),
  ].join(',');
  const BUY_NOW_SELECTORS = (CFG.buyNowSelectors || []).join(',');
  const BUY_NOW_TEXT = /^\s*buy\s*now\s*$/i; // fallback until the real Fastrr selector is filled in

  const nativeFetch = window.fetch.bind(window);
  let cart = CFG.cart || null;
  let suppressAddUntil = 0; // stops a Fastrr-internal /cart/add from double-firing items_added after BUY NOW
  let lastCheckoutAt = 0;

  const measure = (name, data) => window.oaiq('measure', name, data);
  const toInt = (n) => parseInt(n, 10) || 0;

  const toContent = (id, name, quantity, unitAmount) => ({
    id: String(id),
    name: name || '',
    content_type: 'product',
    quantity: Math.max(1, toInt(quantity)),
    amount: toInt(unitAmount),
    currency: CURRENCY,
  });

  const contentsEvent = (contents, amount) => ({
    type: 'contents',
    amount: amount != null ? toInt(amount) : contents.reduce((s, c) => s + c.amount * c.quantity, 0),
    currency: CURRENCY,
    contents,
  });

  const cartItemToContent = (i) =>
    toContent(i.variant_id || i.id, i.product_title || i.title, i.quantity, i.final_price != null ? i.final_price : i.price);

  const refreshCart = () =>
    nativeFetch(`${ROOT}cart.js`, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((c) => { cart = c; })
      .catch(() => {});

  // --- Attribution: Pixel cookies -> hidden cart attributes -----------------
  const readCookie = (name) => {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? m[1] : '';
  };

  const syncAttribution = () => {
    const wanted = { _oai_oppref: readCookie('__oppref'), _oai_obref: readCookie('__obref') };
    const current = (cart && cart.attributes) || {};
    const diff = {};
    Object.keys(wanted).forEach((k) => {
      if (wanted[k] && current[k] !== wanted[k]) diff[k] = wanted[k]; // raw value, unmodified (per OpenAI spec)
    });
    if (!Object.keys(diff).length) return;
    nativeFetch(`${ROOT}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: diff }),
    })
      .then((r) => r.json())
      .then((c) => { cart = c; })
      .catch(() => {});
  };

  // --- items_added: intercept every AJAX /cart/add (theme, drawer, apps) ----
  const isAddUrl = (u) => /\/cart\/add(\.js)?(\?|$)/.test(String(u || ''));

  const onAdded = (data) => {
    if (Date.now() < suppressAddUntil || !data) return;
    const items = (Array.isArray(data.items) ? data.items : [data]).filter((i) => i && i.variant_id);
    if (!items.length) return;
    measure('items_added', contentsEvent(items.map(cartItemToContent)));
    refreshCart().then(syncAttribution);
  };

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const req = nativeFetch(input, init);
    if (isAddUrl(url)) {
      req.then((res) => { if (res.ok) res.clone().json().then(onAdded).catch(() => {}); }).catch(() => {});
    }
    return req;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__oaiqAdd = isAddUrl(url);
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__oaiqAdd) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) {
          try { onAdded(JSON.parse(this.responseText)); } catch (e) { /* non-JSON response */ }
        }
      });
    }
    return xhrSend.apply(this, arguments);
  };

  // --- checkout_started ------------------------------------------------------
  const fireCheckoutStarted = (event) => {
    const now = Date.now();
    if (now - lastCheckoutAt < 2000) return; // multiple handlers / double taps
    lastCheckoutAt = now;
    measure('checkout_started', event);
  };

  const buyNowEvent = (el) => {
    const form = el.closest('form') || document.querySelector('form[action*="/cart/add"]');
    const idField = form && form.querySelector('[name="id"]');
    const qtyField = form && form.querySelector('[name="quantity"]');
    const p = CFG.product;
    const variantId = (idField && idField.value) || (p && p.selectedVariantId);
    if (!variantId || !p) return null;
    const price = p.variants[variantId] != null ? p.variants[variantId] : 0;
    return contentsEvent([toContent(variantId, p.title, qtyField ? qtyField.value : 1, price)]);
  };

  const isBuyNow = (el) => {
    if (BUY_NOW_SELECTORS && el.closest(BUY_NOW_SELECTORS)) return true;
    const btn = el.closest('button, a, [role="button"]');
    return !!btn && BUY_NOW_TEXT.test(btn.textContent || '');
  };

  // Capture phase on window: runs before Fastrr's own click handlers open checkout.
  window.addEventListener('click', (e) => {
    const el = e.target && e.target.closest ? e.target : null;
    if (!el) return;

    if (isBuyNow(el)) {
      const ev = buyNowEvent(el);
      if (!ev) return;
      suppressAddUntil = Date.now() + 4000;
      measure('items_added', ev);
      fireCheckoutStarted(ev);
      syncAttribution();
      return;
    }

    if (el.closest(CHECKOUT_SELECTORS)) {
      const items = (cart && cart.items) || [];
      if (!items.length) return;
      fireCheckoutStarted(contentsEvent(items.map(cartItemToContent), cart.total_price));
      syncAttribution();
    }
  }, true);

  // The SDK writes __oppref/__obref after it loads — retry a couple of times.
  syncAttribution();
  setTimeout(syncAttribution, 2000);
  setTimeout(syncAttribution, 6000);
})();
